"use client"

import type { HeroType, Hero } from "@/types/hero.types"
import { getItemDefinition } from "@/systems/item-system"
import type { GameStats } from "@/types/game.types"
import type { HeroInventory } from "@/types/item.types"
import { PlayIcon } from "@/components/icons/pixel-icons"
import { NineSliceButton } from "@/components/ui/nine-slice-button"
import { useViewportHeight } from "@/hooks/use-viewport-height"
import { ShopBottomBar } from "@/components/shop-bottom-bar"
import { useMemo, useState, useCallback } from "react"

type ShopViewMode = "shop" | "quest"
import { getRandomMerchant } from "@/data/merchant-types"
import { ShopBackground } from "@/components/game-over/shop-background"
import { ShopWindow } from "@/components/game-over/shop-window"
import { Billboard } from "@/components/game-over/billboard"
import { MerchantDisplay } from "@/components/game-over/merchant-display"
import { LivesDisplay } from "@/components/game-ui/hud/lives-display"
import { GoldDisplay } from "@/components/game-ui/hud/gold-display"
import { HeroDisplay } from "@/components/game-over/hero-display"
import { playShopOpenSound } from "@/hooks/use-sound-effects"

// Animation duration in milliseconds
const WALK_OUT_DURATION = 2000

interface LevelCompleteScreenProps {
  wave: number
  score: number
  currentWaveIndex: number
  totalWaves: number
  currentLevelIndex: number
  totalLevels: number
  onRestart: (heroType: HeroType) => void
  onNextLevel?: () => void
  onOpenMap?: () => void
  isMobile?: boolean
  isDesktop?: boolean
  heroTypes: typeof import("@/data/hero-types").heroTypes
  selectedHeroType: string
  hero: Hero | null
  heroInventory?: HeroInventory
  stats: GameStats
  onBuyItem?: (itemDefId: string, price: number) => void
  onBuyCloneCharge?: (cost: number) => void
  onSpendGold?: (amount: number) => void
  onSellItem?: (slotIndex: number, sellPrice: number) => void
}

export const LevelCompleteScreen = ({
  currentLevelIndex,
  totalLevels,
  onNextLevel,
  onOpenMap,
  isMobile = false,
  isDesktop = false,
  heroTypes,
  selectedHeroType,
  hero,
  heroInventory,
  stats,
  onBuyItem,
  onBuyCloneCharge,
  onSpendGold,
  onSellItem,
}: LevelCompleteScreenProps) => {
  const isLastLevel = currentLevelIndex === totalLevels - 1
  const viewportHeight = useViewportHeight()
  const [isWalkingOut, setIsWalkingOut] = useState(false)
  const [shopView, setShopView] = useState<ShopViewMode>("shop")
  const [showBillboardBubble, setShowBillboardBubble] = useState(true)
  const [showShopBubble, setShowShopBubble] = useState(true)

  const merchant = useMemo(() => getRandomMerchant(), [])

  // Randomly determine shopkeep buy rate: 35%, 50%, or 65%
  const shopRate = useMemo(() => {
    const rates = [0.35, 0.5, 0.65]
    return rates[Math.floor(Math.random() * rates.length)]
  }, [])

  const handleBillboardClick = useCallback(() => {
    setShopView("quest")
    setShowBillboardBubble(false)
  }, [])

  const handleShopFrontClick = useCallback(() => {
    playShopOpenSound()
    setShopView("shop")
    setShowShopBubble(false)
  }, [])

  const handleToggleView = useCallback(() => {
    setShopView((prev) => prev === "shop" ? "quest" : "shop")
    setShowBillboardBubble(false)
    setShowShopBubble(false)
  }, [])

  const handleSellItem = useCallback((slotIndex: number) => {
    if (!onSellItem || !heroInventory) return

    const slot = heroInventory.slots[slotIndex]
    if (!slot) return

    const itemDef = getItemDefinition(slot.itemDefId)
    if (!itemDef) return

    const sellPrice = Math.floor(itemDef.cost * shopRate)
    onSellItem(slotIndex, sellPrice)
  }, [onSellItem, heroInventory, shopRate])

  const handleNextLevel = useCallback(() => {
    if (isWalkingOut) return
    setIsWalkingOut(true)
    // Wait for walk-out animation to complete before transitioning
    setTimeout(() => {
      // After first level, open the map instead of auto-advancing
      if (onOpenMap) {
        onOpenMap()
      } else {
        onNextLevel?.()
      }
    }, WALK_OUT_DURATION)
  }, [isWalkingOut, onNextLevel, onOpenMap])

  return (
    <div
      id="level-complete-screen"
      className="absolute inset-0 flex flex-col z-50"
      style={{
        height: viewportHeight ? `${viewportHeight}px` : "100dvh",
      }}
    >
      <ShopBackground />
      <ShopWindow
        money={stats.money}
        inventory={heroInventory}
        heroLevel={hero?.level || 1}
        onBuyItem={onBuyItem || (() => {})}
        onBuyCloneCharge={onBuyCloneCharge || (() => {})}
        onSpendGold={onSpendGold || (() => {})}
        viewMode={shopView}
        isMobile={isMobile}
        onMessagesClick={() => console.log("Messages clicked")}
        onLeaderboardClick={() => console.log("Leaderboard clicked")}
        onDailyClimbClick={() => console.log("Daily Climb clicked")}
        onDonateClick={() => console.log("Donate clicked")}
        onToggleView={handleToggleView}
      />
      <Billboard onClick={handleBillboardClick} isMobile={isMobile} showBubble={showBillboardBubble} />
      {merchant && (
        <MerchantDisplay
          merchant={merchant}
          shopFrontPosition={{ top: "55%", right: "0" }}
          characterPosition={{ top: "76%", right: "35px", zIndex: 5 }}
          onShopFrontClick={handleShopFrontClick}
          isMobile={isMobile}
          showBubble={showShopBubble}
        />
      )}

      <HeroDisplay
        selectedHeroType={selectedHeroType}
        position={{ top: "67%", zIndex: 5 }}
        isDesktop={isDesktop}
        isWalkingOut={isWalkingOut}
      />

      <div className="absolute top-4 left-4 z-20">
        <LivesDisplay lives={stats.lives} />
      </div>

      <div className="absolute top-4 left-[140px] z-20">
        <GoldDisplay gold={stats.money} />
      </div>

      <ShopBottomBar
        heroTypes={heroTypes}
        selectedHeroType={selectedHeroType}
        hero={hero}
        heroInventory={heroInventory}
        isDesktop={isDesktop}
        merchant={merchant}
        shopRate={shopRate}
        onSellItem={handleSellItem}
      />

      <div className="absolute bottom-28 left-0 right-0 flex flex-col items-center justify-center gap-3 z-50">
        {/* Shop rate pill - stacked above the Next Level button so the two
            can never overlap (it used to be absolutely positioned underneath) */}
        <div
          className={`px-4 py-1.5 rounded-lg font-bold text-sm pointer-events-none ${
            shopRate === 0.65 ? 'bg-green-900/80 text-green-300' :
            shopRate === 0.5 ? 'bg-yellow-900/80 text-yellow-300' :
            'bg-red-900/80 text-red-300'
          }`}
          style={{
            textShadow: "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 0 4px #000",
          }}
        >
          Shopkeep buying at {Math.round(shopRate * 100)}%
        </div>
        {!isLastLevel && onNextLevel && !isWalkingOut && (
          <div className="relative">
            {/* Particle effects at top */}
            <div className="absolute -top-2 left-0 right-0 flex justify-center gap-3 pointer-events-none">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 bg-amber-300 rounded-full animate-top-sparkle"
                  style={{ animationDelay: `${i * 0.5}s` }}
                />
              ))}
            </div>
            <NineSliceButton id="next-level-button" onClick={handleNextLevel} className="text-2xl">
              <span className="text-black">Next Level</span>
              <PlayIcon size={24} />
            </NineSliceButton>
          </div>
        )}
      </div>
    </div>
  )
}
